import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowLeft, Pencil } from "lucide-react";
import { getUsers } from "../../utils/getUser";
import { base64ToFile, convertDateFormatInUtc } from "../../utils/util";
import { getUserByid } from "../../reducers/authentication.slice";
import { AuthContext } from "../../context/authContext";
import { TIER_CONFIG, getTierKey, stringToColor } from "../../components/member/tierConfig";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import useMediaQuery from "../../components/shared/useMediaQuery";

function formatAddress(addressObj) {
  if (!addressObj) return "";
  const parts = [
    addressObj.address,
    addressObj.building,
    [addressObj.city, addressObj.state, addressObj.zipcode].filter(Boolean).join(", "),
  ].filter(Boolean);
  return parts.join(" , ");
}

function InfoRow({ label, value }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  if (value == null || value === "") return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 5 : 16,
        padding: "13px 0",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <span style={{ fontSize: 12.5, color: "#c3c8cc" }}>{label}</span>
      <span style={{ fontSize: 13.5, color: "#fff", textAlign: isMobile ? "left" : "right", maxWidth: "100%", overflowWrap: "anywhere" }}>{value}</span>
    </div>
  );
}

export default function MemberProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [user, setUser] = useState({});
  const [imgOpen, setImgOpen] = useState(false);

  useEffect(() => {
    setUser(getUsers());
    const userId = getUsers()?.id;
    dispatch(getUserByid({ userId })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      const newUserData = {
        ...getUsers(),
        discord_id: res?.payload?.discord_id,
        discord_role: res?.payload?.discord_role,
        permission_details: res?.payload?.permission_details,
        address_details: res?.payload?.address_details,
        payment_details: res?.payload?.payment_details,
        first_name: res?.payload?.first_name,
        last_name: res?.payload?.last_name,
        profile_picture: res?.payload?.profile_picture,
        role: res?.payload?.role,
        ip_address: res?.payload?.ip_address,
        last_login: res?.payload?.last_login,
        date_of_joining: res?.payload?.date_of_joining,
        login_type: res?.payload?.login_type,
        is_discord_verified: res?.payload?.is_discord_verified,
      };
      setUser(newUserData);
      localStorage.setItem("user", JSON.stringify(newUserData));
    });
  }, []);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();
  const tier = TIER_CONFIG[getTierKey(user)] || TIER_CONFIG.member;
  const profileImgUrl = user?.profile_picture
    ? URL.createObjectURL(base64ToFile(user.profile_picture, "file", "image/*"))
    : null;

  return (
    <div style={{ width: "100%", maxWidth: 560, margin: "0 auto", display: "flex", flexDirection: "column", gap: isMobile ? 16 : 20 }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          width: "fit-content",
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <PageHeader
        title="My Profile"
        actions={
          <button
            type="button"
            onClick={() => navigate("edit", { state: { user_detail: user, isPaymentVia: false } })}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 34,
              height: 34,
              borderRadius: 9,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <Pencil size={15} />
          </button>
        }
      />

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: isMobile ? 18 : 24,
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 14 : 18,
        }}
      >
        <button
          type="button"
          onClick={() => profileImgUrl && setImgOpen(true)}
          style={{
            width: 68,
            height: 68,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.15)",
            background: profileImgUrl ? "transparent" : stringToColor(fullName || "RSC"),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
            padding: 0,
            cursor: profileImgUrl ? "pointer" : "default",
          }}
        >
          {profileImgUrl ? (
            <img src={profileImgUrl} alt={fullName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{initials || "?"}</span>
          )}
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", textTransform: "capitalize" }}>{fullName || "—"}</div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              marginTop: 6,
              padding: "3px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.03em",
              color: tier.color,
              background: `${tier.color}26`,
            }}
          >
            <span>{tier.emoji}</span> {tier.label}
          </div>
        </div>
      </div>

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 16,
          padding: isMobile ? "2px 16px" : "4px 20px",
          background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        }}
      >
        <InfoRow label="Email" value={user?.email} />
        <InfoRow label="IP Address" value={user?.ip_address} />
        <InfoRow label="Status" value={user?.status && <StatusBadge status={user.status === "ACTIVE" ? "ACTIVE" : "INACTIVE"} label={user.status} />} />
        <InfoRow label="Login Type" value={user?.login_type?.toLowerCase()} />
        <InfoRow label="Discord Username" value={user?.discord_id} />
        <InfoRow label="Address" value={formatAddress(user?.address_details)} />
        <InfoRow
          label="Signup Date"
          value={user?.date_of_joining && convertDateFormatInUtc(user.date_of_joining, "MMMM D, YYYY hh:mm:ss A")}
        />
        <InfoRow
          label="Last Login"
          value={user?.last_login && convertDateFormatInUtc(user.last_login, "MMMM D, YYYY hh:mm:ss A")}
        />
      </div>

      <Dialog open={imgOpen} onOpenChange={setImgOpen}>
        <DialogContent style={{ maxWidth: 420, background: "rgba(0,0,0,0.9)", padding: 16 }}>
          {profileImgUrl && (
            <img src={profileImgUrl} alt={fullName} style={{ width: "100%", borderRadius: 10, objectFit: "contain", maxHeight: "70vh" }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
